import { useAppSelector, useAppDispatch } from 'hooks';
import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Text,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
} from '@chakra-ui/react';

import Card from 'components/card/Card';

import FormInput from 'components/fields/auto/FormInput';
import SelectInput from 'components/fields/auto/SelectInput';
import SelectSearch from 'components/fields/SelectSearch';
import useIsBinderDisabled from 'views/auto_form/hooks/useIsBinderDisabled';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import {
  changeCompanyAndBrokerInformation,
  addMissingMandatoryField,
  removeMissingMandatoryField,
} from 'reducers/autoPersonalFormReducer';

import { SECTIONS } from 'views/auto_form/constants';

import type { APIBranchData, APIProducerCodeData } from 'types/api.types';

import { apiGetMyBranches, apiGetMyProducerCodes } from 'api';

const InsuranceCompanyInformation = () => {
  const dispatch = useAppDispatch();

  const isBinderDisabled = useIsBinderDisabled();
  const isBranchWideCodesEnabled = useFeatureFlag(
    'enable-branch-wide-producer-codes',
  );

  const {
    data: producerCodes = [],
    isLoading: loadingProducerCodes,
    isFetching: isFetchingProducerCodes,
  } = useQuery<APIProducerCodeData[]>({
    queryKey: ['producer-codes-all'],
    queryFn: () => apiGetMyProducerCodes(null, 'all'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const {
    data: myBranches = [],
    isLoading: loadingMyBranches,
    isFetching: isFetchingMyBranches,
  } = useQuery<APIBranchData[]>({
    queryKey: ['my-branches-all'],
    queryFn: () => apiGetMyBranches(null, 'all'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const data = useAppSelector(
    (state) => state.autoPersonalForm.data.company_and_broker_information,
  );

  useEffect(() => {
    if (
      isBranchWideCodesEnabled &&
      (!data?.producer_code || data.producer_code === '')
    ) {
      dispatch(
        addMissingMandatoryField({
          name: 'Producer Code',
          section: SECTIONS.INSURANCE_COMPANY_INFORMATION,
        }),
      );
    } else if (isBranchWideCodesEnabled) {
      dispatch(removeMissingMandatoryField('Producer Code'));
    }
  }, [data?.producer_code, isBranchWideCodesEnabled, dispatch]);

  const onCompanyAndBrokerInformationChange = (event: any) => {
    const { name, value } = event.target;

    dispatch(changeCompanyAndBrokerInformation({ name, value }));
  };

  const isBrokerCodeDisabled = () => {
    const isWawanesa = data.insurance_company
      .toLowerCase()
      .includes('wawanesa');
    const isAvivaJourney = data.insurance_company === 'Aviva - Aviva Journey';

    if (isWawanesa || isAvivaJourney) return false;

    return true;
  };

  return (
    <Card mb="20px" id="sectionInsuranceCompanyInformation">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Insurance Company and Broker Information
      </Text>

      <SimpleGrid columns={4} spacing="20px">
        <FormInput
          section={SECTIONS.INSURANCE_COMPANY_INFORMATION}
          label="Insurance Company (Insurer)"
          name="insurance_company"
          value={data.insurance_company}
          onChange={onCompanyAndBrokerInformationChange}
          required
          disabled
        />
        <FormInput
          section={SECTIONS.INSURANCE_COMPANY_INFORMATION}
          label="Broker"
          name="broker"
          value={data.broker}
          onChange={onCompanyAndBrokerInformationChange}
          required
          disabled
        />
        <FormInput
          section={SECTIONS.INSURANCE_COMPANY_INFORMATION}
          label="Broker Code"
          name="broker_code"
          value={data.broker_code}
          onChange={onCompanyAndBrokerInformationChange}
          required
          disabled={isBrokerCodeDisabled()}
        />
        {(() => {
          const branchesLoading = loadingMyBranches || isFetchingMyBranches;
          const initialBranchMissing =
            !branchesLoading &&
            !!data.broker_branch &&
            !myBranches.some((branch) => branch.name === data.broker_branch);

          return (
            <FormControl>
              <FormLabel ml="0">Branch</FormLabel>
              {initialBranchMissing ? (
                <>
                  <Input value={data.broker_branch} isDisabled />
                  <Text color="red.500" fontSize="sm" mt={1}>
                    You cannot change the branch because the initially selected
                    branch does not match your available branches.
                  </Text>
                </>
              ) : (
                <SelectSearch
                  name="broker_branch"
                  value={data.broker_branch}
                  onChange={(value) => {
                    const event = {
                      target: {
                        name: 'broker_branch',
                        value: value || '',
                        type: 'text',
                      },
                    };
                    onCompanyAndBrokerInformationChange(event);
                  }}
                  options={myBranches.map((branch) => ({
                    value: branch.name,
                    label: branch.name,
                  }))}
                  placeholder="Search..."
                  isDisabled={branchesLoading || myBranches.length <= 1}
                  isLoading={branchesLoading}
                />
              )}
            </FormControl>
          );
        })()}
        {isBinderDisabled ? (
          <FormInput
            section={SECTIONS.INSURANCE_COMPANY_INFORMATION}
            label="Producer Code"
            name="producer_code"
            value={data.producer_code}
            onChange={onCompanyAndBrokerInformationChange}
            required
            disabled
          />
        ) : isBranchWideCodesEnabled ? (
          <FormControl isRequired>
            <div id="Producer Code">
              <FormLabel ml="0">Producer Code</FormLabel>
              <SelectSearch
                name="producer_code"
                value={data.producer_code}
                onChange={(value) => {
                  const event = {
                    target: {
                      name: 'producer_code',
                      value: value || '',
                      type: 'text',
                    },
                  };
                  onCompanyAndBrokerInformationChange(event);
                }}
                options={producerCodes.map((producerCode) => ({
                  value: producerCode.name,
                  label: producerCode.name,
                }))}
                placeholder="Search..."
                isDisabled={loadingProducerCodes || isFetchingProducerCodes}
                isLoading={loadingProducerCodes || isFetchingProducerCodes}
              />
            </div>
          </FormControl>
        ) : (
          <SelectInput
            section={SECTIONS.INSURANCE_COMPANY_INFORMATION}
            label="Producer Code"
            name="producer_code"
            value={data.producer_code}
            options={[
              { value: '', label: 'Select...' },
              ...producerCodes.map((producerCode) => ({
                value: producerCode.name,
                label: producerCode.name,
              })),
            ]}
            onChange={onCompanyAndBrokerInformationChange}
            required
            disabled={loadingProducerCodes || isFetchingProducerCodes}
          />
        )}
      </SimpleGrid>
    </Card>
  );
};

export default InsuranceCompanyInformation;
